/// <reference path="./column-meta.d.ts" />
// These are the important styles to make sticky column pinning work!
// Apply styles like this using your CSS strategy of choice with this kind of logic to head cells, data cells, footer cells, etc.

import type { Column, ColumnDef } from '@tanstack/react-table';
import type { CSSProperties } from 'react';

// View the index.css file for more needed styles such as border-collapse: separate
const getCommonPinningStyles = <TData>(column: Column<TData, unknown>): CSSProperties => {
  let filter: string | undefined;
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn = isPinned === 'left' && column.getIsLastColumn('left');
  const isFirstRightPinnedColumn = isPinned === 'right' && column.getIsFirstColumn('right');

  if (isLastLeftPinnedColumn) {
    filter = 'drop-shadow(1px 0 0px #DDE5ED)';
  } else if (isFirstRightPinnedColumn) {
    filter = 'drop-shadow(-1px 0px 0px #DDE5ED)';
  } else {
    filter = undefined;
  }

  return {
    filter,
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    position: isPinned ? 'sticky' : 'relative',
    width: column.getSize(),
    backgroundColor: isPinned ? 'white' : undefined,
    zIndex: isPinned ? 10 : 0,
  };
};

/** Minimum column width (px) so short headers stay readable. */
const HEADER_MIN_WIDTH_BASE = 120;
/** Maximum column width (px) so headers do not force more than 2 lines; longer titles get this cap. */
const HEADER_MIN_WIDTH_MAX = 360;
/** Horizontal padding (px) applied to header cell (e.g. px-4). */
const HEADER_PADDING = 16;
/** Space (px) reserved for sort/dropdown icon + gap when header has dropdown (justify-between layout). */
const HEADER_SORT_ICON_AND_GAP = 28;
/** Extra space (px) when header has a popover/help icon (e.g. ?) so text stays within 2 lines. */
const HEADER_POPOVER_ICON_AND_GAP = 24;
/** Fixed width (px) for actions column so it closely hugs its button. */
const ACTIONS_COLUMN_WIDTH = 64;

/**
 * Default header font used for DataTable header measurement (must match UI typography).
 */
export const DATA_TABLE_HEADER_FONT = '500 14px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

/**
 * Measures text width for a given font, remaining safe during SSR.
 */
const measureTextWidth = (text: string, font: string = DATA_TABLE_HEADER_FONT): number => {
  if (typeof document === 'undefined') return HEADER_MIN_WIDTH_BASE;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return HEADER_MIN_WIDTH_BASE;
  ctx.font = font;
  return ctx.measureText(text).width;
};

/**
 * Computes the minimum column width so the header text fits in at most 2 lines (no line-clamp).
 * Reserves space for sort/dropdown icon (and optionally popover/help icon) so columns don't wrap to 3 lines.
 * Uses total text width and longest-word width so wrapping stays within 2 lines in pt-BR and en-US.
 */
export const getHeaderMinWidthForTitle = (title: string | undefined, font: string = DATA_TABLE_HEADER_FONT, options?: { hasPopover?: boolean }): number => {
  if (!title) return HEADER_MIN_WIDTH_BASE;
  const iconSpace = HEADER_SORT_ICON_AND_GAP + (options?.hasPopover ? HEADER_POPOVER_ICON_AND_GAP : 0);
  const textWidth = measureTextWidth(title, font);
  const fullWidth = textWidth + HEADER_PADDING * 2 + iconSpace;
  const words = title.split(/\s+/).filter(Boolean);
  const longestWord = words.length ? Math.max(...words.map((w) => measureTextWidth(w, font))) : 0;
  const byTotal = fullWidth / 1.25;
  const byWord = longestWord * 1.5;
  const min = Math.ceil(Math.max(byTotal, byWord));
  return Math.min(HEADER_MIN_WIDTH_MAX, Math.max(HEADER_MIN_WIDTH_BASE, min));
};

type ColumnMetaWithWidth = {
  name?: string;
  minWidth?: number;
  headerFont?: string;
  /**
   * When true, reserves extra space for a header popover/help icon (?) so the title fits in at most 2 lines.
   */
  headerHasPopover?: boolean;
  /**
   * Optional sample text (or typical content) used to derive a minimum width
   * based on body cell content instead of (or in addition to) the header.
   */
  cellTextSample?: string;
  /**
   * Optional font used for body cell width measurement.
   */
  cellFont?: string;
  /**
   * Maximum number of lines allowed for body cell text when calculating width.
   * Defaults to 2 if provided; ignored when `cellTextSample` is undefined.
   */
  maxCellLines?: number;
};

type ColumnMeta = Record<string, unknown> & ColumnMetaWithWidth;

const DEFAULT_CELL_MAX_LINES = 2;

/**
 * Utility to compute a minimum width for body cell text given a font.
 * This mirrors the header behaviour but allows different font/line constraints.
 */
export const getCellMinWidthForText = (
  text: string | undefined,
  { font = DATA_TABLE_HEADER_FONT, maxLines = DEFAULT_CELL_MAX_LINES, paddingX = HEADER_PADDING }: { font?: string; maxLines?: number; paddingX?: number } = {}
): number => {
  if (!text) return HEADER_MIN_WIDTH_BASE;
  const fullWidth = measureTextWidth(text, font) + paddingX * 2;
  const words = text.split(/\s+/).filter(Boolean);
  const longestWord = words.length ? Math.max(...words.map((w) => measureTextWidth(w, font))) : 0;

  // Heuristic: distribute full width across the allowed number of lines,
  // but also respect the longest single word so it does not overflow.
  const byTotal = fullWidth / Math.max(1, maxLines);
  const byWord = longestWord * 1.2;
  const min = Math.ceil(Math.max(byTotal, byWord));
  return Math.min(HEADER_MIN_WIDTH_MAX, Math.max(HEADER_MIN_WIDTH_BASE, min));
};

/**
 * Returns columns with meta.minWidth and size set so header text fits in at most 2 lines.
 * Uses meta.name for font-based width; explicit meta.minWidth wins. Final size is max(column.size, computed min).
 */
export const augmentColumnsWithHeaderMinWidth = <TData, TValue>(columns: ColumnDef<TData, TValue>[]): ColumnDef<TData, TValue>[] =>
  columns.map((col) => {
    const meta = (col.meta || {}) as ColumnMeta;
    const name = meta?.name;

    // Explicit minWidth from meta always wins.
    if (meta.minWidth != null) {
      const currentSize = (col as { size?: number }).size ?? Number.MAX_SAFE_INTEGER;
      const size = currentSize === Number.MAX_SAFE_INTEGER ? meta.minWidth : Math.max(currentSize, meta.minWidth);
      const minSize = meta.minWidth;
      return { ...col, size, minSize, meta } as unknown as ColumnDef<TData, TValue>;
    }

    if (!name) return col;

    const headerFont = meta.headerFont ?? DATA_TABLE_HEADER_FONT;
    const minWidth = getHeaderMinWidthForTitle(name, headerFont, { hasPopover: meta.headerHasPopover });
    const currentSize = (col as { size?: number }).size ?? Number.MAX_SAFE_INTEGER;
    const size = currentSize === Number.MAX_SAFE_INTEGER ? minWidth : Math.max(currentSize, minWidth);

    return { ...col, size, minSize: minWidth, meta: { ...meta, minWidth } } as unknown as ColumnDef<TData, TValue>;
  });

/**
 * Enforces standard flags for any column with `id === 'actions'`.
 * Actions columns are always: non-hideable, non-sortable, non-resizable,
 * non-filterable, and not manually pinnable (programmatic pinning still works).
 */
export const normalizeActionsColumn = <TData, TValue>(columns: ColumnDef<TData, TValue>[]): ColumnDef<TData, TValue>[] =>
  columns.map((col) => {
    if (col.id !== 'actions') return col;
    const meta = (col.meta || {}) as ColumnMeta;
    return {
      ...col,
      size: ACTIONS_COLUMN_WIDTH,
      enableHiding: false,
      enableSorting: false,
      enableResizing: false,
      enableColumnFilter: false,
      enableGlobalFilter: false,
      enablePinning: false,
      meta: { ...meta, minWidth: ACTIONS_COLUMN_WIDTH },
    };
  });

export { getCommonPinningStyles };
